import { Stepper, StepperCompact } from '@/components/shared/Stepper';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface HeaderWithStepperProps {
  stepDefinitions: { id: string; title: string }[];
  currentStep: number;
  goToStep: (stepIndex: number) => void;
  prefetchingStepIndices: Set<number>;
  aiEnrichmentState: { status: 'idle' | 'running' | 'error' };
  /** When true, always render the full vertical Stepper instead of
      switching to StepperCompact below md. Used by the mobile Sheet
      drawer in DynamicSmartFlowContent. */
  forceFullStepper?: boolean;
}

const HeaderWithStepper = ({
  stepDefinitions,
  currentStep,
  goToStep,
  prefetchingStepIndices,
  aiEnrichmentState,
  forceFullStepper = false,
}: HeaderWithStepperProps) => {
  const { t } = useTranslation();
  return (
    <div className="px-4 pt-4 pb-4 w-full md:w-70 xl:w-90">
      <div className="space-y-4">
        {stepDefinitions.length > 0 ? (
          <>
            <div className={forceFullStepper ? '' : 'hidden md:block'}>
              <Stepper
                key={`stepper-${stepDefinitions.length}-${currentStep}`}
                steps={stepDefinitions}
                currentStep={currentStep}
                onStepClick={goToStep}
                allowNavigation
                loadingSteps={prefetchingStepIndices}
              />
            </div>
            {!forceFullStepper && (
              <div className="md:hidden">
                <StepperCompact
                  key={`stepper-compact-${stepDefinitions.length}-${currentStep}`}
                  steps={stepDefinitions}
                  currentStep={currentStep}
                  loadingSteps={prefetchingStepIndices}
                />
              </div>
            )}
          </>
        ) : (
          <div className="h-20 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--brand-primary))]" />
          </div>
        )}

        <AnimatePresence>
          {aiEnrichmentState.status !== 'idle' && (
            <motion.div
              key={`ai-indicator-${aiEnrichmentState.status}`}
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-xs sm:text-sm shadow-sm ${
                aiEnrichmentState.status === 'running'
                  ? 'border-[hsl(var(--brand-primary))]/30 bg-[hsl(var(--brand-primary))]/5 text-[hsl(var(--brand-primary))]'
                  : 'border-amber-200 bg-amber-50 text-amber-800'
              }`}
              aria-live="polite"
            >
              {aiEnrichmentState.status === 'running' ? (
                <>
                  <div className="w-8 h-8 rounded-lg bg-[hsl(var(--brand-primary))]/10 flex items-center justify-center shrink-0">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium truncate">
                      {t('EMPLOYMENTAGREEMENT_SMARTFLOW_AIINDICATOR_RUNNINGTITLE')}
                    </span>
                    <span className="text-sm text-[hsl(var(--brand-muted))] truncate">
                      {t('EMPLOYMENTAGREEMENT_SMARTFLOW_AIINDICATOR_RUNNINGDESCRIPTION')}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium">
                      {t('EMPLOYMENTAGREEMENT_SMARTFLOW_AIINDICATOR_ERRORTITLE')}
                    </span>
                    <span className="text-sm">
                      {t('EMPLOYMENTAGREEMENT_SMARTFLOW_AIINDICATOR_ERRORDESCRIPTION')}
                    </span>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default HeaderWithStepper;
